import { useState } from "react";
import { useNavigate } from "react-router";
import { Phone, Delete } from "lucide-react";
import { cn } from "../../lib/utils";
import { href } from "../../util";
import { useUIStore } from "../../stores/uiStore";

const KEYS = [
	["1", "2", "3"],
	["4", "5", "6"],
	["7", "8", "9"],
	["*", "0", "#"],
];

export const DialPad = ({
	accountId,
	client,
	onClose,
}: {
	accountId: string;
	client: any;
	onClose: () => void;
}) => {
	const [input, setInput] = useState("");
	const navigate = useNavigate();
	const { setActiveCall } = useUIStore();

	const press = (k: string) => setInput((v) => v + k);
	const del = () => setInput((v) => v.slice(0, -1));

	const handleMessage = () => {
		if (!input.trim()) return;
		navigate(href("/accounts/:accountId/chats/:chatId", { accountId, chatId: input.trim() }));
		onClose();
	};

	const handleCall = async () => {
		const jid = input.trim();
		if (!jid) return;
		try {
			await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch (err) {
			console.error("[dial] mic permission denied:", err);
			return;
		}
		try {
			const chat = client?.getChat?.(jid);
			if (!chat) {
				console.warn("[dial] no chat for JID:", jid);
				return;
			}
			const session = (chat as any)?.startCall?.(true, false);
			if (session) {
				setActiveCall({ session, isIncoming: false, chatId: jid, accountId, hasVideo: false, screenSharing: false });
				session.ring?.();
				onClose();
			} else {
				console.error("[dial] startCall returned no session for:", jid);
			}
		} catch (err) {
			console.error("[dial] startCall failed:", err);
		}
	};

	return (
		// Overlay covers just the sidebar area on mobile
		<div className="absolute inset-0 z-30 flex flex-col justify-end" style={{ backdropFilter: "blur(4px)" }}>
			{/* Tap-to-dismiss backdrop */}
			<div className="flex-1" onClick={onClose} />

			{/* Dial pad sheet */}
			<div className="glass-liquid rounded-t-3xl px-6 pt-5 pb-2 flex flex-col gap-4"
				style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>

				{/* Number display */}
				<div className="flex items-center justify-between min-h-[3rem]">
					<span className={cn(
						"text-3xl font-light tracking-wider flex-1",
						input ? "text-white" : "text-white/25",
					)}>
						{input || "Enter a JID or number"}
					</span>
					{input && (
						<button
							onClick={del}
							onContextMenu={(e) => { e.preventDefault(); setInput(""); }}
							className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white"
							title="Delete (hold to clear)"
						>
							<Delete size={20} />
						</button>
					)}
				</div>

				{/* Key grid */}
				<div className="grid grid-cols-3 gap-3">
					{KEYS.flat().map((k) => (
						<button
							key={k}
							onClick={() => press(k)}
							className="h-14 rounded-2xl glass-raised text-white text-xl font-medium active:scale-95 transition-transform hover:bg-white/15"
						>
							{k}
						</button>
					))}
				</div>

				{/* Action row */}
				<div className="flex gap-3">
					<button
						onClick={handleCall}
						disabled={!input.trim()}
						className="flex-1 h-13 rounded-full bg-green-500 hover:bg-green-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white font-medium transition-colors"
					>
						<Phone size={18} />
						Call
					</button>
					<button
						onClick={handleMessage}
						disabled={!input.trim()}
						className="flex-1 h-13 rounded-full bg-primary hover:bg-primary-light disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium transition-colors"
					>
						Message
					</button>
				</div>

				{/* Dismiss */}
				<button
					onClick={onClose}
					className="w-full py-2 text-sm text-white/40 hover:text-white/70 transition-colors"
				>
					Cancel
				</button>
			</div>
		</div>
	);
};
